import { useEffect, useState } from "react";
import { api, errText } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { toast } from "sonner";
import { Loader2, ShieldCheck } from "lucide-react";

export const BAND_SWATCH = {
  emerald: "bg-emerald-500", green: "bg-green-500", amber: "bg-amber-500",
  orange: "bg-orange-500", red: "bg-red-500", slate: "bg-slate-400", blue: "bg-blue-500",
};

export function TrustBandsDialog({ open, onClose, onSaved }) {
  const [bands, setBands] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) {
      setBands(null);
      api.get("/admin/trust-bands").then((r) => setBands(r.data.bands)).catch((e) => toast.error(errText(e)));
    }
  }, [open]);
  const update = (i, field, val) => setBands((bs) => bs.map((b, idx) => idx === i ? { ...b, [field]: val } : b));
  const save = async () => {
    setSaving(true);
    try {
      const payload = { bands: bands.map((b) => ({ ...b, min: parseInt(b.min, 10) || 0 })) };
      const { data } = await api.put("/admin/trust-bands", payload);
      setBands(data.bands);
      toast.success("Trust bands updated");
      onSaved?.();
      onClose();
    } catch (e) { toast.error(errText(e)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg bg-white" data-testid="trust-bands-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-[#12234A] flex items-center gap-2"><ShieldCheck size={18} className="text-[#F5551E]" /> Trust bands</DialogTitle>
          <DialogDescription className="text-slate-500">Rename each band and set the minimum score it starts at. The lowest band always starts at 0.</DialogDescription>
        </DialogHeader>
        {!bands ? (
          <div className="flex justify-center py-8"><Loader2 className="animate-spin text-[#F5551E]" size={24} /></div>
        ) : (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-[11px] font-semibold text-slate-400 px-1">
              <span className="w-4" /><span className="flex-1">Band name</span><span className="w-28 text-right">Minimum score</span>
            </div>
            {bands.map((b, i) => (
              <div key={b.key} className="flex items-center gap-2" data-testid={`band-row-${b.key}`}>
                <span className={`h-3 w-3 rounded-full shrink-0 ${BAND_SWATCH[b.color] || "bg-slate-400"}`} />
                <Input value={b.label} onChange={(e) => update(i, "label", e.target.value)} data-testid={`band-label-${b.key}`} className="flex-1 h-9 rounded-lg" />
                <Input type="number" min="0" max="100" value={b.min} disabled={i === bands.length - 1} onChange={(e) => update(i, "min", e.target.value)} data-testid={`band-min-${b.key}`} className="w-28 h-9 rounded-lg text-right disabled:opacity-60" />
              </div>
            ))}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={onClose} className="rounded-xl">Cancel</Button>
              <Button onClick={save} disabled={saving} data-testid="save-trust-bands" className="rounded-xl bg-[#F5551E] hover:bg-[#E04812] text-white">{saving ? <Loader2 size={15} className="animate-spin" /> : "Save bands"}</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
